import * as THREE from 'three'
import type { Combatant } from './simulation'
import type { AnimationMotionContext, AnimationSemantic } from './animationVariation'
import type { LivingState } from './lifeOmega'
import { characterDNAMotorBias } from './characterDNA'
import { cognitiveMotorBias, type CognitiveAgentState } from './cognitiveSkillAgency'
import {
  MotorFieldRuntime,
  motorActionFromAnimationState,
  type MotorFieldFrame,
  type MotorThoughtState,
} from './motionField'

export type FighterMotorRig={
  hips:THREE.Object3D|null
  spine:THREE.Object3D|null
  chest:THREE.Object3D|null
  neck:THREE.Object3D|null
  head:THREE.Object3D|null
  leftShoulder:THREE.Object3D|null
  rightShoulder:THREE.Object3D|null
}

const norm=(name:string)=>name.toLowerCase().replace(/[^a-z0-9]/g,'')
function findBone(root:THREE.Object3D,aliases:readonly string[]){
  const wanted=aliases.map(norm)
  let exact:THREE.Object3D|null=null,suffix:THREE.Object3D|null=null
  root.traverse(node=>{
    if(!(node as THREE.Bone).isBone||!node.name)return
    const n=norm(node.name)
    if(!exact&&wanted.includes(n))exact=node
    if(!suffix&&wanted.some(a=>a.length>=4&&n.endsWith(a)))suffix=node
  })
  return exact??suffix
}

export function resolveFighterMotorRig(root:THREE.Object3D):FighterMotorRig{
  return{
    hips:findBone(root,['hips','pelvis','rootpelvis']),
    spine:findBone(root,['spine','spine1','spine01']),
    chest:findBone(root,['chest','spine2','spine02','upperchest']),
    neck:findBone(root,['neck','neck1','neck01']),
    head:findBone(root,['head','head1']),
    leftShoulder:findBone(root,['leftshoulder','shoulderl','lshoulder','leftclavicle']),
    rightShoulder:findBone(root,['rightshoulder','shoulderr','rshoulder','rightclavicle']),
  }
}

export function createFighterMotorField(fighterId:string,identitySuffix:string){
  return new MotorFieldRuntime(`fight:${fighterId}:${identitySuffix}`,'humanoid')
}

export function fighterHarmonicVisualBias(frame:MotorFieldFrame){
  return{
    motionPulse01:THREE.MathUtils.clamp(Math.abs(frame.harmonic.motionPulse),0,1),
    vfxEmission01:THREE.MathUtils.clamp(frame.harmonic.vfxEmission,0,1),
    coherence01:THREE.MathUtils.clamp(frame.harmonic.phaseCoherence,0,1),
  }
}

export function motorThoughtFromFighter(
  state:Combatant,
  motion:AnimationMotionContext,
  life:LivingState,
  semantic:AnimationSemantic,
  fighterId:string,
  mind?:CognitiveAgentState,
):Partial<MotorThoughtState>{
  const health01=state.maxHealth>0?THREE.MathUtils.clamp(state.health/state.maxHealth,0,1):0
  const stamina01=THREE.MathUtils.clamp(state.stamina/100,0,1)
  const attack=Boolean(state.attackPhase)
  const guarding=state.blocking||state.parryWindow>0
  const airborne=Math.abs(state.y)>0.025?1:0
  const cognitive=mind?cognitiveMotorBias(mind):null
  const dna=characterDNAMotorBias(fighterId)
  return{
    action:motorActionFromAnimationState(semantic),
    speed01:THREE.MathUtils.clamp(motion.speedRatio/1.35,0,1),
    urgency:THREE.MathUtils.clamp(Math.max((attack?.52:0)+(guarding?.24:0)+(1-health01)*.22,cognitive?.urgency??0),0,1),
    arousal:THREE.MathUtils.clamp(Math.max(life.stress*.58+(1-stamina01)*.18+(attack?.3:0),cognitive?.arousal??0),0,1),
    confidence:THREE.MathUtils.clamp((.3+health01*.34+life.consciousness*.22+life.balance*.14)*.54+dna.confidence*.26+(cognitive?.confidence??dna.confidence)*.2,0,1),
    fatigue:THREE.MathUtils.clamp(Math.max(life.fatigue,1-stamina01),0,1),
    pain:THREE.MathUtils.clamp(life.pain,0,1),
    curiosity:THREE.MathUtils.clamp(dna.curiosity*.62+(cognitive?.curiosity??dna.curiosity)*.38,0,1),
    threat:THREE.MathUtils.clamp(Math.max((state.hitstun>0?.7:0)+(guarding?.28:0),cognitive?.threat??0),0,1),
    socialWarmth:0,
    focus:THREE.MathUtils.clamp((.55+life.sensoryConfidence*.3+(attack?.12:0))*.5+dna.focus*.28+(cognitive?.focus??dna.focus)*.22,0,1),
    turn01:THREE.MathUtils.clamp(motion.vz*.12,-1,1),
    slope01:0,
    airborne01:airborne,
    contactIntent01:attack?THREE.MathUtils.clamp(.68+life.proprioception*.25,0,1):0,
    targetBearing01:0,
  }
}

export function clearFighterMotorOffsets(offsets:Map<THREE.Object3D,THREE.Quaternion>){
  for(const [bone,delta] of offsets){
    bone.quaternion.multiply(delta.clone().invert()).normalize()
  }
  offsets.clear()
}

function add(
  offsets:Map<THREE.Object3D,THREE.Quaternion>,
  bone:THREE.Object3D|null,
  x:number,y:number,z:number,
){
  if(!bone)return
  const delta=new THREE.Quaternion().setFromEuler(new THREE.Euler(x,y,z,'XYZ'))
  bone.quaternion.multiply(delta).normalize()
  offsets.set(bone,delta)
}

export function applyFighterMotorFrame(
  rig:FighterMotorRig,
  frame:MotorFieldFrame,
  offsets:Map<THREE.Object3D,THREE.Quaternion>,
  weight=1,
){
  const w=THREE.MathUtils.clamp(weight,0,1)
  const sway=THREE.MathUtils.clamp(frame.lateralSway*1.55,-.05,.05)*w
  const core=THREE.MathUtils.clamp(frame.coreShift*.032,-.04,.04)*w
  const lean=THREE.MathUtils.clamp(frame.forwardLean*.5,-.1,.1)*w
  const lead=THREE.MathUtils.clamp(frame.turnLead*.42,-.085,.085)*w
  const head=THREE.MathUtils.clamp(frame.headLead+frame.gazeMicro,-.14,.14)*w
  const shoulder=THREE.MathUtils.clamp((frame.limbSwing-.4)*.025,-.025,.035)*w

  add(offsets,rig.hips,lean*.18,lead*.22,sway*.42+core*.2)
  add(offsets,rig.spine,lean*.28,lead*.15,-sway*.3)
  add(offsets,rig.chest,lean*.22,lead*.2,-sway*.18)
  add(offsets,rig.neck,-lean*.06,head*.3,sway*.1)
  add(offsets,rig.head,-lean*.035,head*.68,-sway*.08)
  add(offsets,rig.leftShoulder,shoulder,0,-sway*.2)
  add(offsets,rig.rightShoulder,-shoulder,0,-sway*.2)
}

